export const capabilityModel = {
    headline: 'AI、全栈与数据系统的技术领导力',
    intro: '以 AI、全栈工程与数据算法为核心，贯通产品定义、系统架构和关键研发；统筹技术路线、团队建设与业务交付。',
    groups: [
        {label: 'AI 与产品工程', dimensions: ['ai', 'fullstack']},
        {label: '数据与算法', dimensions: ['data', 'algorithms']},
        {label: '技术与组织统筹', dimensions: ['strategy', 'organization']}
    ],
    dimensions: [
        {
            id: 'ai',
            short: 'AI 系统架构',
            name: 'Agent 架构与知识工程',
            thesis: '从模型应用到可控的智能系统',
            description: '结合大模型、知识检索与任务编排设计业务 Agent，明确模型推理、工具执行和人工决策的分工，覆盖知识服务、数据分析与多模态生成。',
            decision: '以业务语义与本体组织知识，设计检索、上下文和记忆机制；围绕任务状态、工具权限与失败恢复约束执行，建立分层评测。',
            mechanism: '主导 Agent 架构、关键链路研发与公共框架建设，统筹模型选择、系统集成和质量验证，平衡效果、响应时间与使用成本。',
            practice: '主导企业知识平台、智能问数与 Game Factory 建设，将语言工程、任务编排和偏好对齐沉淀为 AI LAB 共用架构与研发规范。',
            links: [
                {label: '企业知识平台', href: '#project-knowledge'},
                {label: 'Agent 公共平台', href: '#project-platform'},
                {label: 'Game Factory', href: '#project-gamefactory'}
            ]
        },
        {
            id: 'fullstack',
            short: '全栈产品工程',
            name: '产品体验与端到端系统设计',
            thesis: '贯通交互、应用与服务架构',
            description: '兼具交互设计、前端与客户端、后端服务及部署经验，能够从完整用户流程拆解系统，协调界面体验、业务逻辑与运行架构。',
            decision: '设计前后端职责、接口与数据模型，处理异步任务、状态同步和访问权限；结合运行隔离、故障恢复与可观测性保障系统运行。',
            mechanism: '从产品原型到系统架构组织方案设计，承担关键研发，协调客户端、服务端与模型能力的集成，统一功能验证和交付要求。',
            practice: '主导 Game Factory 架构与关键链路研发，推进 Talent Mining 本地客户端与在线协作；负责过多端 KOL 平台的后端建设。',
            links: [
                {label: 'Game Factory', href: '#project-gamefactory'},
                {label: 'Talent Mining', href: '#project-talent'},
                {label: '多端 KOL 平台', href: '#project-youkeai'}
            ]
        },
        {
            id: 'data',
            short: '数据平台建设',
            name: '数据架构与高性能计算',
            thesis: '从业务数据到实时分析服务',
            description: '围绕经营分析与高频业务事件，设计数据采集、实时处理、分层存储和分析服务，将指标口径、处理能力与可靠性纳入统一架构。',
            decision: '掌握流式计算、批量计算与索引设计，处理状态恢复、重复与迟到数据、事务一致性等问题，结合负载验证吞吐和资源效率。',
            mechanism: '主导数据平台技术路线与核心计算能力建设，统筹数据模型、质量规则和业务服务，推进数据研发、运营分析与业务验收协同。',
            practice: '主导 LatticeFlow 实时数据平台建设，已具备约 3.3 万条/秒处理能力，承接约 1.78 亿条/日源事件；推进问数、经营指标与业务监控交付。',
            links: [
                {label: 'LatticeFlow', href: '#project-lattice'},
                {label: '关系索引系统', href: '#project-bilm'},
                {label: '智能问数', href: '#project-query'}
            ]
        },
        {
            id: 'algorithms',
            short: '算法与决策',
            name: '推荐系统、用户建模与优化',
            thesis: '把业务目标转化为可验证的算法',
            description: '结合推荐算法、用户行为建模与优化方法，将业务目标转化为特征、排序目标和策略约束，贯通模型评估与业务系统实现。',
            decision: '具备多路召回、语义匹配、社交图推荐和多目标排序经验，结合用户分群、强化学习与因果分析研究策略效果及适用条件。',
            mechanism: '确定建模目标、数据条件和评估方法，组织策略与工程的协同实现；区分离线指标、模拟结果和实际业务表现，依据验证结果迭代方案。',
            practice: '研发达人智选的多路召回与社交联合推荐，主导用户分群与受约束推荐策略；参与基于强化学习的碳中和政策模拟研究。',
            links: [
                {label: '达人智选', href: '#project-kol'},
                {label: '用户建模与推荐', href: '#project-global-recommendation'},
                {label: '强化学习研究', href: '#project-carbon'}
            ]
        },
        {
            id: 'strategy',
            short: '技术战略',
            name: '产品规划、架构演进与技术投入',
            thesis: '以技术判断支撑产品与投资决策',
            description: '结合业务需求与技术可行性规划产品方向，将 AI 应用、数据服务和共用平台纳入整体技术路线，确定关键研发投入与阶段交付范围。',
            decisionLabel: '核心决策',
            decision: '评估自研、采购与复用方案，明确产品差异化所需的核心能力；综合性能、可靠性、维护成本及团队条件判断架构取舍。',
            mechanism: '通过场景验证和架构评审确定建设优先级，统筹人力、模型及基础设施资源，依据交付进展与业务反馈调整投入。',
            practice: '统筹 AI LAB 多业务产品布局与公共能力建设，组织游戏孵化的一年期投入测算；推动数据服务整合，相关建设与成本优化每年节省至少 86 万元。',
            links: [
                {label: 'AI LAB 工作经历', href: '#resume-experience-ov'},
                {label: '公共能力平台', href: '#project-platform'},
                {label: '数据服务与成本优化', href: '#project-query'}
            ]
        },
        {
            id: 'organization',
            short: '产研组织',
            name: '技术团队建设与研发质量',
            thesis: '让团队持续承担复杂系统研发',
            description: '围绕 AI 与数据业务建设团队，衔接岗位分工、技术培养和项目责任，组织产品、研发、测试及业务部门协同推进交付。',
            decisionLabel: '组织设计',
            decision: '依据系统复杂度与团队能力配置研发职责，识别关键岗位和培养重点；明确架构评审、质量验证、发布准入与问题处置责任。',
            mechanism: '通过技术招聘、方案辅导与阶段培养提升成员判断能力，以研发 SOP 统一需求、设计、实现和审查要求，将项目经验沉淀为团队知识。',
            practice: '负责 AI LAB 从零建设与数据团队统筹，组织技术岗位评估、首月培养和研发规范建设；承担 Game Factory 技术负责与团队日常管理。',
            links: [
                {label: 'AI LAB 团队职责', href: '#resume-experience-ov'},
                {label: '研发体系', href: '#project-platform'},
                {label: '团队知识建设', href: '#project-project-knowledge'}
            ]
        }
    ]
};
